import { PlayerDataRow, TaskDataRow } from '../../../../contexts/ReportsContextHelper'
import { Player, ReceivedAnswer, Subtask } from '../../../../types/commonTypes'
import { formatTime } from '../../../../util/number'
import { getHtmlText } from '../../../../util/string'

export type ExcelDataRow = {
  exerciseName: string
  maxPoints: number | string
  team: string
  email: string
  teamMemberNames: string
  answer: string
  points: number | string
  feedback: string
}

export function getAnswerText(ex: Subtask, a: ReceivedAnswer) {
  let replyString: string

  try {
    switch (ex.type) {
      case 'CreativeExercise':
        replyString = '<div>' + a.answer + '</div>'
        const charsToRemove = ['\u001E', '\u0013']
        charsToRemove.forEach((ch) => {
          while (replyString.indexOf(ch) > -1) {
            replyString = replyString.replace(ch, '')
          }
        })
        const parser = new DOMParser()
        const doc = parser.parseFromString(replyString, 'text/html')

        const imgCount = doc.querySelectorAll('img').length
        const videoCount = doc.querySelectorAll('video').length
        const audioCount = doc.querySelectorAll('audio').length

        const textPart = getHtmlText(replyString)
        const imagePart = imgCount > 0 ? 'Images: ' + imgCount : ''
        const videoPart = videoCount > 0 ? 'Videos: ' + videoCount : ''
        const audioPart = audioCount > 0 ? 'Audios: ' + audioCount : ''
        return [textPart, imagePart, videoPart, audioPart].filter((p) => p.length > 0).join('\r\n')

      case 'MultichoiceExercise':
        if ((a.answer || '').length > 0) return a.answer
        else {
          if ((a.answerIndex || a.answerIndex === 0) && ex.data.answers[a.answerIndex].text)
            return ex.data.answers[a.answerIndex].text
          else return (a.answerIndex ?? 0) + 1 + '. option'
        }

      case 'PollExercise':
        replyString = ''
        if (a.answerIndexes) {
          a.answerIndexes.forEach((item) => {
            if (ex.data.answers[item].text.length > 0) replyString += ex.data.answers[item].text + ','
            else replyString += item + 1 + '. option,'
          })
          return replyString.slice(0, -1)
        } else return ''

      case 'MissingWordExercise':
        const missingWordAnswer = a.answer as unknown as any
        return Object.keys(missingWordAnswer)
          .map((key, index) => {
            return missingWordAnswer[key] + '=' + ex.data.answers[index].word
          })
          .join(',')

      case 'CombineExercise':
        replyString = ''
        try {
          a.answer.split(';').forEach((pair) => {
            const left = parseInt(pair.split(',')[0])
            const right = parseInt(pair.split(',')[1])
            const leftString = ex.data.answers[left].left.text || left + '. option'
            const rightString = ex.data.answers[right].right.text || right + '. option'
            if (left === right) {
              replyString += leftString + ' = ' + rightString + ' | '
            } else {
              replyString += leftString + ' ~ ' + rightString + ' | '
            }
          })
          return replyString
        } catch (e) {
          return ''
        }

      case 'ExploreExercise':
        if (a.answer === 'true') return 'Player has confirmed having reviewed the contents.'
        else return ''
      default:
        return ''
    }
  } catch (e) {
    return ''
  }
}

export function getPlayerAnswersSheetData(
  tasksData: TaskDataRow[],
  players: Player[],
  noPointsGame?: boolean,
): { columns: any[]; rowData: ExcelDataRow[] } {
  const columns = noPointsGame
    ? [
        { header: 'Task Name', key: 'exerciseName' },
        { header: 'Player/Team Name', key: 'team' },
        { header: 'Player email', key: 'email' },
        { header: 'Team member names', key: 'teamMemberNames' },
        { header: 'Answer', key: 'answer' },
        { header: 'Feedback', key: 'feedback' },
      ]
    : [
        { header: 'Task Name', key: 'exerciseName' },
        { header: 'Max Points', key: 'maxPoints' },
        { header: 'Player/Team Name', key: 'team' },
        { header: 'Player email', key: 'email' },
        { header: 'Team member names', key: 'teamMemberNames' },
        { header: 'Answer', key: 'answer' },
        { header: 'Points', key: 'points' },
        { header: 'Feedback', key: 'feedback' },
      ]

  const rowData: ExcelDataRow[] = []
  tasksData?.forEach((task) => {
    task.task.subtasks.forEach((subtask, index) => {
      subtask.answers?.forEach((answer) => {
        const player = players.find((p) => p.id === answer.userId && !p.deleted)
        if (player) {
          const dataRow: ExcelDataRow = {
            exerciseName: task.name + (task.task.subtasks.length > 1 ? ` (${index + 1})` : ''),
            maxPoints: subtask.maxPoints,
            team: player.nickName,
            email: player.email,
            teamMemberNames: player.teamMemberNames,
            answer: getAnswerText(subtask, answer),
            points: answer.points,
            feedback: answer.feedbackMessage ?? '',
          }
          rowData.push(dataRow)
        }
      })
    })
  })

  return { columns, rowData }
}

export function getScoreSummarySheetData(
  tasksData: TaskDataRow[],
  players: Player[],
  noPointsGame?: boolean,
): { columns: any[]; rowData: ExcelDataRow[] } {
  const columns: any[] = [{ header: 'Player/Team Name', key: 'playerName' }]
  let gameMaxPoints = 0

  tasksData.forEach((task) => {
    gameMaxPoints += task.maxPoints
    task.task.subtasks.forEach((subtask, index) => {
      columns.push({
        header:
          task.name +
          (task.task.subtasks.length > 1 ? ` (${index + 1})` : '') +
          (noPointsGame ? '' : '\r\nMax points: ' + subtask.maxPoints),
        key: subtask.id,
      })
    })
  })
  if (!noPointsGame) {
    columns.push({ header: 'Total points\r\nMax points: ' + gameMaxPoints, key: 'totalPoints' })
    columns.push({ header: '% Score', key: 'scorePercentage', style: { numFmt: '0%' } })
  }

  const rowData: any[] = []
  players
    .filter((p) => !p.deleted)
    .forEach((p) => {
      const data: { [key: string | number]: any } = { playerName: p.nickName }
      let totalPoints = 0
      tasksData?.forEach((task) => {
        task.task.subtasks.forEach((subtask, index) => {
          subtask.answers
            ?.filter((a) => a.userId === p.id)
            .forEach((answer) => {
              data[subtask.id ?? 0] = noPointsGame ? '*' : answer.points
              totalPoints += answer.points
            })
        })
      })
      data['totalPoints'] = totalPoints
      data['scorePercentage'] = Math.ceil((100 * totalPoints) / gameMaxPoints) / 100
      rowData.push(data)
    })
  return { columns, rowData }
}

export function getPlayersSheetData(
  playersData: PlayerDataRow[],
  gameMaxPoints: number,
  noPointsGame?: boolean,
): { columns: any[]; rowData: ExcelDataRow[] } {
  const columns: any[] = noPointsGame
    ? [
        { header: 'Rank', key: 'rank' },
        { header: 'Player/Team Name', key: 'playerName' },
        { header: 'Correct %', key: 'correctPercentage', style: { numFmt: '0%' } },
        { header: 'Incorrect %', key: 'incorrectPercentage', style: { numFmt: '0%' } },
        { header: 'Started', key: 'createdAt' },
        { header: 'Completed', key: 'completedAt' },
        { header: 'Time Spent (HH:MM:SS)', key: 'timeSpent' },
        { header: 'Completion Rate %', key: 'completionRate', style: { numFmt: '0%' } },
        { header: 'Player email', key: 'email' },
        { header: 'Team member names', key: 'teamMemberNames' },
      ]
    : [
        { header: 'Rank', key: 'rank' },
        { header: 'Player/Team Name', key: 'playerName' },
        { header: `Total Points\r\nMax points: ${gameMaxPoints}`, key: 'totalPoints' },
        { header: 'Correct %', key: 'correctPercentage', style: { numFmt: '0%' } },
        { header: 'Incorrect %', key: 'incorrectPercentage', style: { numFmt: '0%' } },
        { header: 'Started', key: 'createdAt' },
        { header: 'Completed', key: 'completedAt' },
        { header: 'Time Spent (HH:MM:SS)', key: 'timeSpent' },
        { header: 'Completion Rate %', key: 'completionRate', style: { numFmt: '0%' } },
        { header: 'Player email', key: 'email' },
        { header: 'Team member names', key: 'teamMemberNames' },
      ]

  const rowData: any[] = playersData.map((row) => {
    return {
      rank: row.rank,
      playerName: row.nickName,
      totalPoints: row.points,
      correctPercentage: Math.ceil(Math.max(100, row.correctPercentage)) / 100,
      incorrectPercentage: (100 - Math.ceil(Math.max(100, row.correctPercentage))) / 100,
      createdAt: row.createdAt.split('.')[0],
      completedAt: (row.completedAt ?? '').split('.')[0],
      timeSpent: formatTime(row.gameDuration),
      completionRate: Math.ceil(row.completionRate) / 100, // (row.overallCompleted ? '✔️ ' : '') +
      email: row.email,
      teamMemberNames: row.teamMemberNames,
    }
  })

  return { columns, rowData }
}

export function getTasksSheetData(
  playersData: TaskDataRow[],
  noPointsGame?: boolean,
): { columns: any[]; rowData: ExcelDataRow[] } {
  const columns: any[] = noPointsGame
    ? [
        { header: 'Task Name', key: 'name' },
        { header: 'Task Type', key: 'type' },
        { header: 'Average Time (HH:MM:SS)', key: 'averageTime' },
        { header: 'Completed', key: 'completedCount' },
        { header: 'Evaluated (x/y)', key: 'evaluatedCount' },
      ]
    : [
        { header: 'Task Name', key: 'name' },
        { header: 'Task Type', key: 'type' },
        { header: 'Average Time (HH:MM:SS)', key: 'averageTime' },
        { header: 'Completed', key: 'completedCount' },
        { header: 'Evaluated (x/y)', key: 'evaluatedCount' },
        { header: 'Average Points', key: 'averagePoints' },
        { header: 'Max Points', key: 'maxPoints' },
        { header: 'Avg. Points %', key: 'avgPointsPercent', style: { numFmt: '0%' } },
      ]

  const rowData: any[] = playersData.map((row) => {
    return {
      name: row.name,
      type: row.type,
      averageTime: formatTime(row.averageTime),
      completedCount: row.completedCount,
      evaluatedCount: row.evaluatedCount + '/' + row.answerCount,
      averagePoints: isNaN(Math.ceil(row.averagePoints)) ? '-' : Math.ceil(row.averagePoints),
      maxPoints: row.maxPoints,
      avgPointsPercent:
        row.evaluatedCount > 0 && !isNaN(row.averageScorePercentage / 100) ? row.averageScorePercentage / 100 : '-',
    }
  })

  return { columns, rowData }
}
